import React, { useState, useEffect } from 'react';
import Button from '../components/Button';
import { useNavigate } from 'react-router-dom';
import supabase from '../integrations/supabase';
import { useAuth } from '../hooks/useAuth';
import { LoginSkeleton } from '../components/SkeletonLoader';
import { useOnboardingTheme } from '../utils/theme-context';

const Login = () => {
  const navigate = useNavigate();
  const { handleOAuthLogin } = useAuth();
  
  // Force light mode for onboarding
  useOnboardingTheme();
  const [authError, setAuthError] = useState('');
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Minimal header for login page
  const header = (
    <header className="w-full flex flex-col items-center justify-center pt-2 pb-1">
      <div className="flex items-center justify-center">
        <img 
                        src={process.env.PUBLIC_URL + '/logos/knoowl-logo.svg'} 
          alt="Knoowl Logo" 
          className="h-24 shadow-xl" 
          style={{ minHeight: '96px', maxHeight: '144px' }}
        />
      </div>
    </header>
  );

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        console.log('🔍 Login: Starting authentication check...');
        setIsCheckingAuth(true);
        
        // Add a timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
          setIsCheckingAuth(false);
        }, 3000); // Reduced to 3 seconds
        
        // Quick Supabase auth check
        let user = null;
        try {
          const { data: { user: supabaseUser } } = await supabase.auth.getUser();
          user = supabaseUser;
        } catch (supabaseError) {
          console.error('❌ Login: Supabase auth error:', supabaseError);
        }
        
        clearTimeout(timeoutId);
        
        if (user) {
          // Quick redirect without backend checks to improve UX
          navigate('/dashboard', { replace: true });
        }
              } catch (error) {
          // Handle auth check error silently
        } finally {
        setIsCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, [navigate]);



  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      setAuthError('');
      
      // Add a timeout to prevent hanging
      const timeoutId = setTimeout(() => {
        setIsGoogleLoading(false);
        setAuthError('Google sign-in timed out. Please try again.');
      }, 10000); // 10 second timeout
      
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });
      
      clearTimeout(timeoutId);
      
      if (error) {
        console.error('Google OAuth error:', error);
        setAuthError('Google sign-in failed. Please try again.');
        setIsGoogleLoading(false);
      } else {
        // User will be redirected to Google
      }
    } catch (error) {
      console.error('Google OAuth error:', error);
      setAuthError('Google sign-in failed. Please try again.');
      setIsGoogleLoading(false);
    }
  };

  const handleRoleSelection = async (role) => {
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Get role_id from master_roles
        const { data: masterRole } = await supabase
          .from('master_roles')
          .select('id')
          .eq('role_name', role)
          .single();
        
        if (masterRole) {
          // Assign role to user
          const { error } = await supabase
            .from('user_roles')
            .insert({
              email: user.email,
              role_id: masterRole.id
            });
          
          if (error) {
            console.error('Role assignment error:', error);
            setAuthError('Failed to assign role. Please try again.');
          } else {
            // Navigate to course selection
            navigate('/course-selection', { replace: true });
          }
        } else {
          setAuthError('Invalid role selected. Please try again.');
        }
      }
    } catch (error) {
      console.error('Role selection error:', error);
      setAuthError('Failed to assign role. Please try again.');
    }
  };



  // Show skeleton while checking authentication
  if (isCheckingAuth) {
    return <LoginSkeleton />;
  }

  // Show skeleton while Google login is in progress
  if (isGoogleLoading) {
    return <LoginSkeleton />;
  }

  // Role Selection Component
  const RoleSelection = () => (
    <div className="w-full max-w-md mx-auto flex flex-col items-center bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-8 mt-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Choose Your Role
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Select whether you're a teacher or student to continue
        </p>
      </div>
      
      <div className="w-full space-y-4">
        <Button 
          animate 
          onClick={() => handleRoleSelection('teacher')}
          className="w-full flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all focus:outline-none hover:scale-105"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span>I'm a Teacher</span>
        </Button>
        
        <Button 
          animate 
          onClick={() => handleRoleSelection('student')}
          className="w-full flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all focus:outline-none hover:scale-105"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span>I'm a Student</span>
        </Button>
      </div>
      
      {authError && (
        <div className="text-sm text-red-500 text-center mt-4">{authError}</div>
      )}
    </div>
  );

  // If role selection is needed, show that instead of login
  if (showRoleSelection) {
    console.log('🔍 Login: Rendering role selection...');
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-black">
        {header}
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
          <RoleSelection />
        </div>
      </div>
    );
  }

  console.log('🔍 Login: Rendering main login form...');
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-black">
      {header}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-md mx-auto flex flex-col items-center bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-6">
          <div className="w-full flex flex-col gap-3">
            <div className="text-center mb-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Currently, only Google Sign-In is available for authentication.
              </p>
            </div>
            <Button 
              animate 
              onClick={handleGoogleLogin} 
              disabled={isGoogleLoading}
              className={`w-full flex items-center justify-center gap-2 bg-black text-white font-bold py-2 px-4 rounded-full shadow transition-all focus:outline-none ${
                isGoogleLoading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
              }`}
            >
              {isGoogleLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Connecting to Google...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 48 48"><g><path fill="#4285F4" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.22l6.85-6.85C36.45 2.36 30.68 0 24 0 14.82 0 6.71 5.1 2.69 12.44l7.98 6.19C12.13 13.13 17.62 9.5 24 9.5z"/><path fill="#34A853" d="M46.1 24.55c0-1.64-.15-3.22-.43-4.74H24v9.01h12.44c-.54 2.9-2.18 5.36-4.64 7.01l7.19 5.6C43.98 37.36 46.1 31.36 46.1 24.55z"/><path fill="#FBBC05" d="M9.67 28.13c-1.13-3.36-1.13-6.99 0-10.35l-7.98-6.19C-1.13 16.36-1.13 31.64 1.69 37.56l7.98-6.19z"/><path fill="#EA4335" d="M24 46c6.68 0 12.45-2.36 16.85-6.44l-7.19-5.6c-2.01 1.35-4.58 2.14-7.66 2.14-6.38 0-11.87-3.63-14.33-8.94l-7.98 6.19C6.71 42.9 14.82 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></g></svg>
                  <span>Sign in with Google</span>
                </>
              )}
            </Button>
            <Button 
              animate 
              disabled={true}
              className="w-full flex items-center justify-center gap-2 bg-gray-400 text-white font-bold py-2 px-4 rounded-full shadow transition-all focus:outline-none cursor-not-allowed opacity-60"
            >
              <svg className="w-5 h-5" viewBox="0 0 448 512" fill="currentColor"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9 114.9-51.3 114.9-114.9S287.7 141 224.1 141zm0 186c-39.5 0-71.5-32-71.5-71.5s32-71.5 71.5-71.5 71.5 32 71.5 71.5-32 71.5-71.5 71.5zm146.4-194.3c0 14.9-12 26.9-26.9 26.9s-26.9-12-26.9-26.9 12-26.9 26.9-26.9 26.9 12 26.9 26.9zm76.1 27.2c-1.7-35.3-9.9-66.7-36.2-92.9S388.6 1.7 353.3 0C317.6-1.7 130.4-1.7 94.7 0 59.4 1.7 28 9.9 1.7 36.2S1.7 123.4 0 158.7c-1.7 35.7-1.7 222.9 0 258.6 1.7 35.3 9.9 66.7 36.2 92.9s57.6 34.5 92.9 36.2c35.7 1.7 222.9 1.7 258.6 0 35.3-1.7 66.7-9.9 92.9-36.2s34.5-57.6 36.2-92.9c1.7-35.7 1.7-222.9 0-258.6zM398.8 388c-7.8 19.6-22.9 34.7-42.5 42.5-29.4 11.7-99.2 9-132.3 9s-102.9 2.6-132.3-9c-19.6-7.8-34.7-22.9-42.5-42.5-11.7-29.4-9-99.2-9-132.3s-2.6-102.9 9-132.3c7.8-19.6 22.9-34.7 42.5-42.5C121.1 2.6 190.9 0 224 0s102.9-2.6 132.3 9c19.6 7.8 34.7 22.9 42.5 42.5 11.7 29.4 9 99.2 9 132.3s2.6 102.9-9 132.3z"/></svg>
              <span>Sign in with Instagram (Coming Soon)</span>
            </Button>
            <Button 
              animate 
              disabled={true}
              className="w-full bg-gray-400 text-white font-bold py-2 px-4 rounded-full shadow transition-all focus:outline-none cursor-not-allowed opacity-60"
            >
              Sign in with Email (Coming Soon)
            </Button>

            <Button 
              animate 
              disabled={true}
              className="w-full bg-gray-400 text-white font-bold py-2 px-4 rounded-full shadow transition-all focus:outline-none cursor-not-allowed opacity-60"
            >
              Sign in with Mobile OTP (Coming Soon)
            </Button>

            {authError && <div className="text-xs text-red-500 text-center mt-2">{authError}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 